import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Clock, Coins, Disc3, Loader2, X } from "lucide-react";
import { formatCoin, getRequiredMinimumPlayers, isValidMinimumPlayersForWinners } from "../../utils/bondWheelHelpers";
import { formatAppDateTime } from "../../utils/appTimeFormat";
import "../../bond-wheel-create.css";

const QUICK_ENTRIES = [500, 1000, 2000, 5000];

const QUICK_DEADLINES = [
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "3h", minutes: 180 },
  { label: "12h", minutes: 720 },
  { label: "24h", minutes: 1440 },
];

const addMinutes = (date, minutes) => new Date(new Date(date).getTime() + minutes * 60 * 1000);

const toLocalInputValue = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatDeadlinePreview = (date) => formatAppDateTime(date);

const QUICK_WINNERS = [1, 2, 3, 5];
const QUICK_MIN_PLAYERS = [2, 3, 5, 10, 20];

const CreateBondWheelModal = ({
  open,
  onClose,
  onSubmit,
  constants,
  userCoinBalance = 0,
  submitting = false,
}) => {
  const minEntry = constants?.minEntryAmount || 500;
  const minMinutes = constants?.minDeadlineMinutes || 30;
  const minWinners = constants?.minNumberOfWinners || 1;
  const maxWinners = constants?.maxNumberOfWinners || 10;
  const minPlayersFloor = constants?.minMinimumPlayers || constants?.minActiveParticipants || 2;
  const maxMinPlayers = constants?.maxMinimumPlayers || 100;

  const [entryAmount, setEntryAmount] = useState(String(minEntry));
  const [numberOfWinners, setNumberOfWinners] = useState(minWinners);
  const [minimumPlayers, setMinimumPlayers] = useState(() =>
    getRequiredMinimumPlayers(minWinners, constants)
  );
  const [deadline, setDeadline] = useState(() => addMinutes(new Date(), minMinutes));
  const [selectedQuickMinutes, setSelectedQuickMinutes] = useState(minMinutes);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) return;
    const defaultDeadline = addMinutes(new Date(), minMinutes);
    setEntryAmount(String(minEntry));
    setNumberOfWinners(minWinners);
    setMinimumPlayers(getRequiredMinimumPlayers(minWinners, constants));
    setDeadline(defaultDeadline);
    setSelectedQuickMinutes(minMinutes);
    setError("");
  }, [open, minEntry, minMinutes, minWinners, constants]);

  const minPlayersRequired = useMemo(
    () => getRequiredMinimumPlayers(numberOfWinners, constants),
    [numberOfWinners, constants]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) handleClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, handleClose, submitting]);

  const numericEntry = Math.floor(Number(entryAmount) || 0);
  const earliestDeadline = useMemo(() => addMinutes(new Date(), minMinutes), [minMinutes]);
  const canAffordEntry = numericEntry <= userCoinBalance && numericEntry >= minEntry;

  const validate = () => {
    if (!numericEntry || numericEntry < minEntry) {
      setError(`Minimum entry is ${formatCoin(minEntry)} COIN.`);
      return false;
    }
    if (numericEntry > userCoinBalance) {
      setError("You do not have enough COIN to host this game.");
      return false;
    }
    if (deadline.getTime() < earliestDeadline.getTime() - 1000) {
      setError(`Deadline must be at least ${minMinutes} minutes from now.`);
      return false;
    }
    if (numberOfWinners < minWinners || numberOfWinners > maxWinners) {
      setError(`Winners must be between ${minWinners} and ${maxWinners}.`);
      return false;
    }
    if (minimumPlayers < minPlayersFloor || minimumPlayers > maxMinPlayers) {
      setError(`Minimum players must be between ${minPlayersFloor} and ${maxMinPlayers}.`);
      return false;
    }
    if (numberOfWinners > minimumPlayers) {
      setError("Winners cannot exceed the minimum player count.");
      return false;
    }
    if (!isValidMinimumPlayersForWinners(numberOfWinners, minimumPlayers)) {
      setError(
        `Minimum players must be greater than twice the number of winners (at least ${minPlayersRequired}).`
      );
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    try {
      await onSubmit({
        entryAmount: numericEntry,
        deadline: deadline.toISOString(),
        numberOfWinners,
        minimumPlayers,
      });
      handleClose();
    } catch (e) {
      setError(e?.message || "Could not create game. Please try again.");
    }
  };

  const handleQuickDeadline = (minutes) => {
    const next = addMinutes(new Date(), minutes);
    setDeadline(next);
    setSelectedQuickMinutes(minutes);
    setError("");
  };

  const handleCustomDeadline = (value) => {
    const next = new Date(value);
    if (Number.isNaN(next.getTime())) return;
    setDeadline(next);
    setSelectedQuickMinutes(null);
    setError("");
  };

  const handleWinnersQuick = (value) => {
    setNumberOfWinners(value);
    setMinimumPlayers((mp) => Math.max(mp, getRequiredMinimumPlayers(value, constants)));
    setError("");
  };

  const handleMinPlayersQuick = (value) => {
    setMinimumPlayers(Math.max(value, minPlayersRequired));
    setError("");
  };

  if (!open) return null;

  return createPortal(
    <div className="bwc-root" role="dialog" aria-modal="true" aria-labelledby="bwc-title">
      <button
        type="button"
        className="bwc-backdrop"
        aria-label="Close"
        onClick={handleClose}
        disabled={submitting}
      />

      <form className="bwc-sheet" onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
        <header className="bwc-head">
          <div className="bwc-head-main">
            <span className="bwc-head-icon">
              <Disc3 size={18} color="#64B5F6" />
            </span>
            <div>
              <p className="bwc-kicker">Bond Wheel</p>
              <h2 id="bwc-title">Create wheel</h2>
            </div>
          </div>
          <div className="bwc-head-balance">
            <Coins size={12} />
            <strong>{formatCoin(userCoinBalance)} COIN</strong>
          </div>
          <button type="button" className="bwc-close" onClick={handleClose} disabled={submitting} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <div className="bwc-body">
          {error ? (
            <div className="bwc-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="bwc-columns">
            <section className="bwc-section">
              <div className="bwc-section-title">Entry amount</div>
              <p className="bwc-section-hint">Everyone pays the same to join. You pay this to host.</p>

              <div className="bwc-entry-field">
                <span className="bwc-entry-badge">COIN</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={entryAmount}
                  onChange={(e) => {
                    setEntryAmount(e.target.value.replace(/[^0-9]/g, ""));
                    setError("");
                  }}
                  placeholder={String(minEntry)}
                  aria-label="Entry amount"
                />
              </div>

              <div className="bwc-chip-grid">
                {QUICK_ENTRIES.map((q) => {
                  const active = numericEntry === q;
                  const disabled = q > userCoinBalance;
                  return (
                    <button
                      key={q}
                      type="button"
                      className={`bwc-chip ${active ? "active" : ""}`}
                      disabled={disabled}
                      onClick={() => {
                        setEntryAmount(String(q));
                        setError("");
                      }}
                    >
                      {formatCoin(q)}
                    </button>
                  );
                })}
              </div>

              <p className="bwc-footnote">Minimum {formatCoin(minEntry)} COIN</p>
            </section>

            <section className="bwc-section">
              <div className="bwc-section-title">Spin deadline</div>
              <p className="bwc-section-hint">Wheel locks shortly before this time, then spins.</p>

              <div className="bwc-chip-grid bwc-chip-grid-deadline">
                {QUICK_DEADLINES.map((d) => (
                  <button
                    key={d.minutes}
                    type="button"
                    className={`bwc-chip ${selectedQuickMinutes === d.minutes ? "active" : ""}`}
                    onClick={() => handleQuickDeadline(d.minutes)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <label className="bwc-datetime">
                <span className="bwc-datetime-label">Custom date & time</span>
                <input
                  type="datetime-local"
                  value={toLocalInputValue(deadline)}
                  min={toLocalInputValue(earliestDeadline)}
                  onChange={(e) => handleCustomDeadline(e.target.value)}
                />
              </label>

              <div className="bwc-deadline-preview">
                <Clock size={13} />
                <span>Spins {formatDeadlinePreview(deadline)}</span>
              </div>
            </section>
          </div>

          <div className="bwc-config-row">
            <section className="bwc-section">
              <div className="bwc-section-title">Number of winners</div>
              <p className="bwc-section-hint">Winners split the pot equally after fees.</p>
              <div className="bwc-stepper">
                <button
                  type="button"
                  className="bwc-stepper-btn"
                  disabled={numberOfWinners <= minWinners}
                  onClick={() => {
                    setNumberOfWinners((v) => {
                      const next = Math.max(minWinners, v - 1);
                      setMinimumPlayers((mp) => Math.max(mp, getRequiredMinimumPlayers(next, constants)));
                      return next;
                    });
                    setError("");
                  }}
                >
                  −
                </button>
                <div className="bwc-stepper-value">
                  <strong>{numberOfWinners}</strong>
                  <span>{numberOfWinners === 1 ? "winner" : "winners"}</span>
                </div>
                <button
                  type="button"
                  className="bwc-stepper-btn"
                  disabled={numberOfWinners >= maxWinners}
                  onClick={() => {
                    setNumberOfWinners((v) => {
                      const next = Math.min(maxWinners, v + 1);
                      setMinimumPlayers((mp) => Math.max(mp, getRequiredMinimumPlayers(next, constants)));
                      return next;
                    });
                    setError("");
                  }}
                >
                  +
                </button>
              </div>
              <div className="bwc-chip-grid bwc-chip-grid-winners">
                {QUICK_WINNERS.filter((q) => q <= maxWinners).map((q) => (
                  <button
                    key={`w-${q}`}
                    type="button"
                    className={`bwc-chip ${numberOfWinners === q ? "active" : ""}`}
                    onClick={() => handleWinnersQuick(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </section>

            <section className="bwc-section">
              <div className="bwc-section-title">Minimum players</div>
              <p className="bwc-section-hint">
                Must exceed twice the winner count ({minPlayersRequired} min for {numberOfWinners}{" "}
                {numberOfWinners === 1 ? "winner" : "winners"}). If not met by deadline, everyone is
                refunded.
              </p>
              <div className="bwc-stepper">
                <button
                  type="button"
                  className="bwc-stepper-btn"
                  disabled={minimumPlayers <= minPlayersRequired}
                  onClick={() => {
                    setMinimumPlayers((v) => Math.max(minPlayersRequired, v - 1));
                    setError("");
                  }}
                >
                  −
                </button>
                <div className="bwc-stepper-value">
                  <strong>{minimumPlayers}</strong>
                  <span>required</span>
                </div>
                <button
                  type="button"
                  className="bwc-stepper-btn"
                  disabled={minimumPlayers >= maxMinPlayers}
                  onClick={() => {
                    setMinimumPlayers((v) => Math.min(maxMinPlayers, v + 1));
                    setError("");
                  }}
                >
                  +
                </button>
              </div>
              <div className="bwc-chip-grid bwc-chip-grid-winners">
                {QUICK_MIN_PLAYERS.filter((q) => q <= maxMinPlayers).map((q) => {
                  const disabled = q < minPlayersRequired;
                  return (
                    <button
                      key={`mp-${q}`}
                      type="button"
                      className={`bwc-chip ${minimumPlayers === q ? "active" : ""}`}
                      disabled={disabled}
                      onClick={() => handleMinPlayersQuick(q)}
                    >
                      {q}
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </div>

        <footer className="bwc-foot">
          <div className="bwc-summary">
            <span>
              {numberOfWinners} win{numberOfWinners === 1 ? "" : "s"} · min {minimumPlayers}
            </span>
            <strong className={canAffordEntry ? "" : "bwc-summary-warn"}>
              {formatCoin(numericEntry)} COIN
            </strong>
          </div>
          <div className="bwc-foot-actions">
            <button type="button" className="bwc-btn bwc-btn-ghost" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="bwc-btn bwc-btn-primary" disabled={submitting || !canAffordEntry}>
            {submitting ? (
              <>
                <Loader2 size={15} className="bwc-spin" />
                Creating…
              </>
            ) : (
              <>Create · {formatCoin(numericEntry)} COIN</>
            )}
            </button>
          </div>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default CreateBondWheelModal;
